import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { ChevronRight, ChevronUp, LayoutGrid } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchTools } from '../../../api/chats';
import { fetchAgentTools } from '../../../api/agents/agent-chats';
import ReactMarkdownMessage from './ReactMarkdownMessage';
import ServiceLogo from '../../common/logo/ServiceLogo';

import styles from './styles/InlineToolsDetails.module.css';

const InlineToolsDetails = ({
    tools = [],
    messageId,
    isUsingTool = false,
    copiedCode,
    onCopyCode,
    setToastMessage,
    toolsRefreshKey = 0,
    isAgentChat = false,
    agentInfo = null,
    guestChatToken = null,
}) => {
    const [allTools, setAllTools] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState(new Set()); // closed by default
    const [savedToolsLength, setSavedToolsLength] = useState(null);
    const [savedIsUsingTool, setSavedIsUsingTool] = useState(false);
    const groupRefs = useRef({});

    // Determine whether tool results can be shown
    const { canShowTools, isTaskOnly } = useMemo(() => {
        if (!isAgentChat) return { canShowTools: true, isTaskOnly: false };
        const isOwner = !!agentInfo?.isOwner;
        const visibility = agentInfo?.toolsVisibility || 'ALL';
        if (isOwner) return { canShowTools: true, isTaskOnly: false };
        if (visibility === 'ALL') return { canShowTools: true, isTaskOnly: false };
        if (visibility === 'TASK_ONLY') return { canShowTools: false, isTaskOnly: true };
        return { canShowTools: false, isTaskOnly: false };
    }, [isAgentChat, agentInfo]);

    // Fetch all tools details
    const fetchAllTools = useCallback(async () => {
        try {
            // Respect agent tool visibility; do not fetch if results are hidden
            if (isAgentChat && !canShowTools) return;

            const toolsData = isAgentChat
                ? await fetchAgentTools(messageId, guestChatToken)
                : await fetchTools(messageId);
            setAllTools(toolsData.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error('Error fetching tools:', error);
        }
    }, [messageId, isAgentChat, canShowTools, guestChatToken]);

    // Keep tools list in sync when its length changes or when processing finishes
    useEffect(() => {
        if (!canShowTools) {
            setSavedIsUsingTool(isUsingTool);
            return;
        }

        const currentLength = tools?.length || 0;

        // Fetch only when there are tools to show OR when processing just finished
        if ((currentLength > 0 && currentLength !== savedToolsLength) || (savedIsUsingTool && !isUsingTool)) {
            fetchAllTools();
            setSavedToolsLength(currentLength);
        }

        setSavedIsUsingTool(isUsingTool);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tools, isUsingTool, canShowTools]);

    // Explicit refetch on refresh key bump (each tool completion)
    useEffect(() => {
        if (!canShowTools) return;
        fetchAllTools();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toolsRefreshKey]);

    // Group consecutive tools from the same service while maintaining order
    const toolGroups = useMemo(() => {
        if (!allTools || !Array.isArray(allTools)) return [];

        return allTools.reduce((acc, tool, index) => {
            const currentServiceName = tool?.tool?.service?.name;
            const prevTool = allTools[index - 1];
            const prevServiceName = prevTool?.tool?.service?.name;

            if (currentServiceName === prevServiceName) {
                // Add to the last group if it's the same service
                acc[acc.length - 1].tools.push(tool);
            } else {
                // Create new group for different service
                acc.push({
                    service: tool?.tool?.service,
                    tools: [tool],
                });
            }
            return acc;
        }, []);
    }, [allTools]);

    const toggleGroup = useCallback(groupIndex => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupIndex)) {
                newSet.delete(groupIndex);
            } else {
                newSet.add(groupIndex);
            }
            return newSet;
        });
    }, []);

    const handleCollapseWithScroll = useCallback(groupIndex => {
        // Scroll to the top of this group before collapsing
        const groupElement = groupRefs.current[groupIndex];
        if (groupElement) {
            groupElement.scrollIntoView({ behavior: 'smooth', block: 'start', duration: 200 });
        }
        // Toggle the group after a small delay for scroll
        setTimeout(() => {
            toggleGroup(groupIndex);
        }, 200);
    }, [toggleGroup]);

    // Show current processing tool
    const currentProcessingTool = useMemo(() => {
        if (!isUsingTool || !tools?.length) return null;
        return tools[tools.length - 1];
    }, [isUsingTool, tools]);

    // If tools are hidden but we are processing, show banner (with logo in TASK_ONLY)
    if (isAgentChat && !canShowTools) {
        return (
            <div className={styles.container}>
                {isUsingTool && (
                    <div className={styles.processingTool}>
                        {isTaskOnly ? (
                            <>
                                <ServiceLogo
                                    logo={currentProcessingTool?.serviceLogo}
                                    name={currentProcessingTool?.serviceName}
                                    size="tiny"
                                />
                                <span className={styles.processingText}>
                                    {currentProcessingTool?.task || 'Processing...'}
                                </span>
                            </>
                        ) : (
                            <>
                                <LayoutGrid size={16} color="var(--color-gray)" />
                                <span className={styles.processingText}>
                                    Tools are running. The agent owner has hidden tool results.
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (!tools || !Array.isArray(tools) || tools.length === 0) {
        return null;
    }

    return (
        <div className={styles.container}>
            {/* Show completed tool groups */}
            {toolGroups?.map((group, groupIndex) => {
                if (!group?.service || !group?.tools) return null;

                const isExpanded = expandedGroups.has(groupIndex);

                return (
                    <div
                        key={groupIndex}
                        className={styles.toolGroup}
                        ref={el => groupRefs.current[groupIndex] = el}
                    >
                        <div
                            className={styles.groupHeader}
                            onClick={() => toggleGroup(groupIndex)}
                        >
                            <div className={styles.groupInfo}>
                                <ServiceLogo
                                    logo={group.service.logo}
                                    name={group.service.name}
                                    size="tiny"
                                />
                                <span className={styles.serviceName}>
                                    {group.service.name}
                                </span>
                                <span className={styles.toolCount}>
                                    {group.tools?.length || 0} tool{(group.tools?.length || 0) !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <motion.div
                                className={styles.expandIcon}
                                animate={{
                                    rotate: isExpanded ? 90 : 0,
                                    x: isExpanded ? '-2px' : 0,
                                }}
                                transition={{ duration: 0.1 }}
                            >
                                <ChevronRight size={16} />
                            </motion.div>
                        </div>

                        <AnimatePresence initial={false}>
                            {isExpanded && (
                                <motion.div
                                    key="content"
                                    className={styles.groupContent}
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                >
                                    {group.tools?.map((tool, toolIndex) => {
                                        if (!tool?.tool?.name) return null;

                                        return (
                                        <div key={toolIndex} className={styles.toolItem}>
                                            <div className={styles.toolName}>{tool?.tool?.name}</div>
                                            <div className={styles.toolResult}>
                                                <ReactMarkdownMessage
                                                    copiedCode={copiedCode}
                                                    onCopyCode={onCopyCode}
                                                    setToastMessage={setToastMessage}
                                                >
                                                    {tool?.result || ''}
                                                </ReactMarkdownMessage>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {isExpanded && (
                            <div
                                className={styles.collapseButton}
                                onClick={() => handleCollapseWithScroll(groupIndex)}
                            >
                                <ChevronUp size={16} />
                            </div>
                        )}
                    </div>
                );
            }) || []}

            {/* Show current processing tool */}
            {currentProcessingTool && (
                <div className={styles.processingTool}>
                    <ServiceLogo
                        logo={currentProcessingTool?.serviceLogo}
                        name={currentProcessingTool?.serviceName}
                        size="tiny"
                    />
                    <span className={styles.processingText}>
                        {currentProcessingTool?.task || 'Processing...'}
                    </span>
                </div>
            )}
        </div>
    );
};

export default memo(InlineToolsDetails);
